import { parseAppMode } from "./app-mode";
import { parseMemberRoles } from "./roles";
import { expectArray, expectNullable, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
export const MEMBER_STATUSES = ["active", "inactive"];
export const STUDIO_STATUSES = ["active", "inactive"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function hasOwn(record, key) {
    return Object.prototype.hasOwnProperty.call(record, key);
}
export function parseMemberStatus(value, path = "memberStatus") {
    return parseStringEnum(value, MEMBER_STATUSES, path);
}
export function parseStudioStatus(value, path = "studioStatus") {
    return parseStringEnum(value, STUDIO_STATUSES, path);
}
export function parseAdminMember(value, path = "adminMember") {
    const record = expectRecord(value, path);
    return {
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        appNickname: expectOptionalString(record.appNickname, `${path}.appNickname`),
        lineDisplayNameSnapshot: expectOptionalString(record.lineDisplayNameSnapshot, `${path}.lineDisplayNameSnapshot`),
        roles: parseMemberRoles(record.roles, `${path}.roles`),
        memberStatus: parseMemberStatus(record.memberStatus, `${path}.memberStatus`),
        defaultAppMode: expectNullable(record.defaultAppMode, parseAppMode, `${path}.defaultAppMode`),
        lastUsedAppMode: expectNullable(record.lastUsedAppMode, parseAppMode, `${path}.lastUsedAppMode`),
        lastAccessAt: expectOptionalString(record.lastAccessAt, `${path}.lastAccessAt`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseAdminMembersResponse(value, path = "adminMembersResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseAdminMember, `${path}.items`)
    };
}
export function parseCreateAdminMemberRequest(value, path = "createAdminMemberRequest") {
    const record = expectRecord(value, path);
    return {
        platformUserId: expectString(record.platformUserId, `${path}.platformUserId`),
        appNickname: expectOptionalString(record.appNickname, `${path}.appNickname`),
        roles: parseMemberRoles(record.roles, `${path}.roles`),
        memberStatus: parseMemberStatus(record.memberStatus, `${path}.memberStatus`),
        defaultAppMode: expectNullable(record.defaultAppMode, parseAppMode, `${path}.defaultAppMode`),
        lastUsedAppMode: expectNullable(record.lastUsedAppMode, parseAppMode, `${path}.lastUsedAppMode`)
    };
}
export function parseUpdateAdminMemberRequest(value, path = "updateAdminMemberRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "appNickname")) {
        parsed.appNickname = expectOptionalString(record.appNickname, `${path}.appNickname`);
    }
    if (hasOwn(record, "roles")) {
        parsed.roles = parseMemberRoles(record.roles, `${path}.roles`);
    }
    if (hasOwn(record, "memberStatus")) {
        parsed.memberStatus = parseMemberStatus(record.memberStatus, `${path}.memberStatus`);
    }
    if (hasOwn(record, "defaultAppMode")) {
        parsed.defaultAppMode = expectNullable(record.defaultAppMode, parseAppMode, `${path}.defaultAppMode`);
    }
    if (hasOwn(record, "lastUsedAppMode")) {
        parsed.lastUsedAppMode = expectNullable(record.lastUsedAppMode, parseAppMode, `${path}.lastUsedAppMode`);
    }
    return parsed;
}
export function parseStudio(value, path = "studio") {
    const record = expectRecord(value, path);
    return {
        studioId: expectString(record.studioId, `${path}.studioId`),
        displayName: expectString(record.displayName, `${path}.displayName`),
        shortName: expectOptionalString(record.shortName, `${path}.shortName`),
        address: expectOptionalString(record.address, `${path}.address`),
        mapUrl: expectOptionalString(record.mapUrl, `${path}.mapUrl`),
        status: parseStudioStatus(record.status, `${path}.status`),
        sortOrder: expectNumber(record.sortOrder, `${path}.sortOrder`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
export function parseStudiosResponse(value, path = "studiosResponse") {
    const record = expectRecord(value, path);
    return {
        items: expectArray(record.items, parseStudio, `${path}.items`)
    };
}
export function parseCreateStudioRequest(value, path = "createStudioRequest") {
    const record = expectRecord(value, path);
    return {
        displayName: expectString(record.displayName, `${path}.displayName`),
        shortName: expectOptionalString(record.shortName, `${path}.shortName`),
        address: expectOptionalString(record.address, `${path}.address`),
        mapUrl: expectOptionalString(record.mapUrl, `${path}.mapUrl`),
        status: parseStudioStatus(record.status, `${path}.status`),
        sortOrder: expectNumber(record.sortOrder, `${path}.sortOrder`)
    };
}
export function parseUpdateStudioRequest(value, path = "updateStudioRequest") {
    const record = expectRecord(value, path);
    const parsed = {};
    if (hasOwn(record, "displayName")) {
        parsed.displayName = expectString(record.displayName, `${path}.displayName`);
    }
    if (hasOwn(record, "shortName")) {
        parsed.shortName = expectOptionalString(record.shortName, `${path}.shortName`);
    }
    if (hasOwn(record, "address")) {
        parsed.address = expectOptionalString(record.address, `${path}.address`);
    }
    if (hasOwn(record, "mapUrl")) {
        parsed.mapUrl = expectOptionalString(record.mapUrl, `${path}.mapUrl`);
    }
    if (hasOwn(record, "status")) {
        parsed.status = parseStudioStatus(record.status, `${path}.status`);
    }
    if (hasOwn(record, "sortOrder")) {
        parsed.sortOrder = expectNumber(record.sortOrder, `${path}.sortOrder`);
    }
    return parsed;
}
